import React, { useCallback, useContext } from 'react';
import { convertUndefinedFields } from 'utils/helpers';
import {
	ICreateModifierGroupsContext,
	CreateModifierGroupsContext,
} from '../../createModifierGroupsState/createModifierGroupsContext';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import ModalSentErrorWithInfo from './ModalSentErrorWithInfo/ModalSentErrorWithInfo';
import { AppContext, IAppContext } from 'appState/appContext';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { createModifierGroups } from 'backend/modifierGroups';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		resetFields,
		changeTypeOfModalWindow,
		showErrorModalWithInfo,
		dataForSubmit,
		errorResponseData,
		typeOfModalWindow,
		fetchedRestaurants,
		fetchedDishes,
	} = useContext<ICreateModifierGroupsContext>(CreateModifierGroupsContext);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow || errorResponseData });

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				changeTypeOfModalWindow(null);
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await createModifierGroups(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.MODIFIER_GROUP_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={resetFields}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={resetFields}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'defaultTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			<ModalSentErrorWithInfo
				onConsentEvent={handleCloseModalWithInfo}
				isVisible={!!errorResponseData}
				errorResponseData={errorResponseData}
				fetchedRestaurants={fetchedRestaurants}
				fetchedDishes={fetchedDishes}
			/>
		</div>
	);
};

export default ModalWindows;
